<template>
  <Teleport to="body">
    <Transition name="loader">
      <div 
        v-if="isLoading" 
        class="loader-overlay"
        :class="{ 'blur-background': blurBackground }"
        role="dialog" 
        aria-modal="true"
        :aria-label="loaderText"
      >
        <div class="loader-container" :class="[`variant-${variant}`, sizeClass]">
          
          <!-- Spinner principal -->
          <div class="spinner-wrapper">
            <div 
              class="spinner" 
              :class="spinnerClass"
              :style="{ 
                '--spinner-size': spinnerSize + 'px',
                '--spinner-color': spinnerColor 
              }"
            >
              <!-- Spinner multi-cercles pour variant "modern" -->
              <template v-if="variant === 'modern'">
                <div class="spinner-ring ring-1"></div>
                <div class="spinner-ring ring-2"></div>
                <div class="spinner-ring ring-3"></div>
              </template>
              
              <!-- Points animés pour variant "dots" -->
              <template v-else-if="variant === 'dots'">
                <div class="dot dot-1"></div>
                <div class="dot dot-2"></div>
                <div class="dot dot-3"></div>
              </template>
              
              <!-- Barres animées pour variant "bars" -->
              <template v-else-if="variant === 'bars'">
                <div class="bar bar-1"></div>
                <div class="bar bar-2"></div>
                <div class="bar bar-3"></div>
                <div class="bar bar-4"></div>
                <div class="bar bar-5"></div>
              </template>
            </div>
          </div>

          <!-- Texte de chargement -->
          <div class="loader-content">
            <h3 class="loader-title" v-if="title">{{ title }}</h3>
            <p class="loader-text">{{ loaderText }}</p>
            
            <!-- Barre de progression optionnelle -->
            <div v-if="showProgress && progress !== null" class="progress-container">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: progress + '%' }"
                ></div>
              </div>
              <span class="progress-text">{{ Math.round(progress) }}%</span>
            </div>
            
            <!-- Sous-texte optionnel -->
            <p v-if="subtitle" class="loader-subtitle">{{ subtitle }}</p>
          </div>

          <!-- Bouton d'annulation optionnel -->
          <button 
            v-if="showCancel && onCancel" 
            type="button"
            class="cancel-button"
            @click="handleCancel"
          >
            Annuler
          </button>
        </div>

        <!-- Animation de fond décorative -->
        <div v-if="showDecorations" class="loader-decorations">
          <div class="decoration-particle particle-1"></div>
          <div class="decoration-particle particle-2"></div>
          <div class="decoration-particle particle-3"></div>
          <div class="decoration-particle particle-4"></div>
          <div class="decoration-particle particle-5"></div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'LoaderDiv',
  props: {
    isLoading: {
      type: Boolean,
      required: true
    },
    loaderText: {
      type: String,
      default: 'Chargement en cours...'
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'classic',
      validator: value => ['classic', 'modern', 'dots', 'bars'].includes(value)
    },
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    color: {
      type: String,
      default: '#3498db'
    },
    blurBackground: {
      type: Boolean,
      default: true
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    progress: {
      type: Number,
      default: null,
      validator: value => value === null || (value >= 0 && value <= 100)
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    onCancel: {
      type: Function,
      default: null
    },
    showDecorations: {
      type: Boolean,
      default: true
    },
    timeout: {
      type: Number,
      default: null // Auto-hide après X millisecondes
    }
  },
  emits: ['cancel', 'timeout'],
  data() {
    return {
      timeoutId: null
    };
  },
  computed: {
    sizeClass() {
      return `size-${this.size}`;
    },
    
    spinnerClass() {
      return `spinner-${this.variant}`;
    },
    
    spinnerSize() {
      const sizes = {
        small: 32,
        medium: 48,
        large: 64
      };
      return sizes[this.size] || sizes.medium;
    },
    
    spinnerColor() {
      return this.color;
    }
  },
  watch: {
    isLoading: {
      handler(newValue) {
        if (newValue && this.timeout) {
          this.startTimeout();
        } else if (!newValue) {
          this.clearTimeout();
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    this.clearTimeout();
  },
  methods: {
    handleCancel() {
      if (this.onCancel) {
        this.onCancel();
      }
      this.$emit('cancel');
    },
    
    startTimeout() {
      this.clearTimeout();
      if (this.timeout && this.timeout > 0) {
        this.timeoutId = setTimeout(() => {
          this.$emit('timeout');
        }, this.timeout);
      }
    },
    
    clearTimeout() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }
  }
};
</script>

<style scoped>
/* Transitions pour l'overlay */
.loader-enter-active,
.loader-leave-active {
  transition: all 0.3s ease;
}

.loader-enter-from,
.loader-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.loader-enter-from .loader-container,
.loader-leave-to .loader-container {
  transform: scale(0.8) translateY(20px);
}

/* Overlay principal */
.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: all 0.3s ease;
}

.loader-overlay.blur-background {
  backdrop-filter: blur(4px);
}

/* Container principal */
.loader-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  max-width: 90vw;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Tailles de container */
.loader-container.size-small {
  padding: 1.5rem;
  min-width: 200px;
}

.loader-container.size-medium {
  padding: 2rem;
  min-width: 280px;
}

.loader-container.size-large {
  padding: 2.5rem;
  min-width: 320px;
}

/* Wrapper du spinner */
.spinner-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

/* Base spinner */
.spinner {
  position: relative;
  width: var(--spinner-size);
  height: var(--spinner-size);
}

/* Variant Classic (cercle simple) */
.spinner-classic {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid var(--spinner-color);
  border-radius: 50%;
  animation: spin-classic 1s linear infinite;
}

@keyframes spin-classic {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Variant Modern (cercles multiples) */
.spinner-modern {
  width: var(--spinner-size);
  height: var(--spinner-size);
}

.spinner-ring {
  position: absolute;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: spin-modern 2s linear infinite;
}

.ring-1 {
  width: 100%;
  height: 100%;
  border-top: 3px solid var(--spinner-color);
  animation-duration: 1.2s;
}

.ring-2 {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  border-right: 3px solid var(--spinner-color);
  animation-duration: 1.8s;
  animation-direction: reverse;
}

.ring-3 {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  border-bottom: 3px solid var(--spinner-color);
  animation-duration: 1.4s;
}

@keyframes spin-modern {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Variant Dots (points animés) */
.spinner-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  background: var(--spinner-color);
  border-radius: 50%;
  animation: bounce-dots 1.4s infinite ease-in-out both;
}

.dot-1 { animation-delay: -0.32s; }
.dot-2 { animation-delay: -0.16s; }
.dot-3 { animation-delay: 0s; }

@keyframes bounce-dots {
  0%, 80%, 100% { 
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% { 
    transform: scale(1.2);
    opacity: 1;
  }
}

/* Variant Bars (barres animées) */
.spinner-bars {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 4px;
  height: var(--spinner-size);
}

.bar {
  width: 6px;
  background: var(--spinner-color);
  border-radius: 3px;
  animation: scale-bars 1.2s infinite ease-in-out;
}

.bar-1 { animation-delay: -1.1s; }
.bar-2 { animation-delay: -1.0s; }
.bar-3 { animation-delay: -0.9s; }
.bar-4 { animation-delay: -0.8s; }
.bar-5 { animation-delay: -0.7s; }

@keyframes scale-bars {
  0%, 40%, 100% { 
    transform: scaleY(0.4);
  }
  20% { 
    transform: scaleY(1);
  }
}

/* Contenu du loader */
.loader-content {
  color: #2c3e50;
}

.loader-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  color: #2c3e50;
}

.loader-text {
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 1rem;
  color: #34495e;
}

.loader-subtitle {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin: 0.5rem 0 0;
  font-style: italic;
}

/* Barre de progression */
.progress-container {
  margin: 1.5rem 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--spinner-color), #2980b9);
  border-radius: 4px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--spinner-color);
  min-width: 40px;
  text-align: right;
}

/* Bouton d'annulation */
.cancel-button {
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
  border: 2px solid rgba(231, 76, 60, 0.3);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.cancel-button:hover {
  background: rgba(231, 76, 60, 0.2);
  border-color: #e74c3c;
  transform: translateY(-1px);
}

.cancel-button:active {
  transform: translateY(0);
}

/* Décorations de fond */
.loader-decorations {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.decoration-particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: float-particles 8s infinite linear;
}

.particle-1 {
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.particle-2 {
  top: 60%;
  right: 15%;
  animation-delay: -2s;
}

.particle-3 {
  bottom: 30%;
  left: 20%;
  animation-delay: -4s;
}

.particle-4 {
  top: 40%;
  right: 30%;
  animation-delay: -6s;
}

.particle-5 {
  bottom: 60%;
  right: 10%;
  animation-delay: -1s;
}

@keyframes float-particles {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 0.8;
  }
}

/* Variants de couleur pour différents états */
.loader-container.variant-success {
  --spinner-color: #27ae60;
}

.loader-container.variant-warning {
  --spinner-color: #f39c12;
}

.loader-container.variant-error {
  --spinner-color: #e74c3c;
}

.loader-container.variant-info {
  --spinner-color: #3498db;
}

/* Responsive Design */
@media (max-width: 768px) {
  .loader-container {
    margin: 1rem;
    padding: 1.5rem;
    border-radius: 16px;
  }
  
  .loader-title {
    font-size: 1.3rem;
  }
  
  .loader-text {
    font-size: 1rem;
  }
  
  .progress-container {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  
  .spinner-bars .bar {
    width: 5px;
  }
}

@media (max-width: 480px) {
  .loader-container.size-large,
  .loader-container.size-medium {
    min-width: auto;
    width: calc(100vw - 2rem);
  }
  
  .loader-title {
    font-size: 1.2rem;
  }
  
  .loader-text {
    font-size: 0.95rem;
  }
  
  .decoration-particle {
    display: none; /* Masquer sur très petits écrans pour les performances */
  }
}

/* Mode sombre */
@media (prefers-color-scheme: dark) {
  .loader-container {
    background: rgba(44, 62, 80, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .loader-title,
  .loader-text {
    color: #ecf0f1;
  }
  
  .loader-subtitle {
    color: #bdc3c7;
  }
  
  .progress-bar {
    background: rgba(255, 255, 255, 0.1);
  }
}

/* Réduction des animations */
@media (prefers-reduced-motion: reduce) {
  .spinner-classic,
  .spinner-ring,
  .dot,
  .bar,
  .decoration-particle,
  .progress-fill::after {
    animation: none;
  }
  
  .loader-enter-active,
  .loader-leave-active,
  .loader-container {
    transition: none;
  }
}

/* Accessibilité */
.loader-container:focus {
  outline: 2px solid var(--spinner-color);
  outline-offset: 4px;
}
</style>
